import { useState } from "react";
import Modal from "@/components/base/Modal";
import { datasetRecords, datasetSampleArticles } from "@/mocks/datasets";

interface DatasetForm {
  name: string;
  description: string;
}

const emptyForm: DatasetForm = { name: "", description: "" };

export default function Datasets() {
  const [datasets, setDatasets] = useState(datasetRecords);
  const [createOpen, setCreateOpen] = useState(false);
  const [dbOpen, setDbOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [form, setForm] = useState<DatasetForm>(emptyForm);
  const [dbForm, setDbForm] = useState({
    dbName: "",
    host: "",
    port: "3306",
    account: "",
    password: "",
  });
  const [testState, setTestState] = useState<"idle" | "testing" | "success">("idle");

  const previewDataset = datasets.find((item) => item.id === previewId) || null;

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const goToDbConfig = () => {
    if (!form.name.trim()) return;
    setDbForm({
      dbName: form.name.trim(),
      host: "",
      port: "3306",
      account: "",
      password: "",
    });
    setTestState("idle");
    setCreateOpen(false);
    setDbOpen(true);
  };

  const testConnection = () => {
    setTestState("testing");
    window.setTimeout(() => setTestState("success"), 900);
  };

  const confirmDataset = () => {
    setDatasets((prev) => [
      ...prev,
      {
        id: `dataset-${Date.now()}`,
        name: form.name.trim() || dbForm.dbName || "新建数据集",
        description: form.description.trim() || "通过数据库导入创建的数据集。",
        source: dbForm.host || "数据库导入",
        host: dbForm.host,
        port: dbForm.port,
        dbName: dbForm.dbName,
        account: dbForm.account,
        articleCount: 0,
        saveType: "数据库导入",
        createTime: "2026/10/13",
        status: 0,
      },
    ]);
    setDbOpen(false);
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground-950">数据加载</h1>
          <p className="mt-1 text-sm text-foreground-500">
            创建数据集并通过数据库导入文章数据，导入完成后可预览数据结构与内容。
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base" />
          创建数据集
        </button>
      </div>

      <div className="mt-8 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs text-foreground-600">
                <th className="px-5 py-3 font-medium">数据集名称</th>
                <th className="px-5 py-3 font-medium">数据描述</th>
                <th className="px-5 py-3 font-medium">数据来源</th>
                <th className="px-5 py-3 font-medium">文章数</th>
                <th className="px-5 py-3 font-medium">状态</th>
                <th className="px-5 py-3 font-medium">创建时间</th>
                <th className="px-5 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {datasets.map((dataset) => (
                <tr
                  key={dataset.id}
                  className="border-b border-background-100 last:border-0 hover:bg-background-100/60"
                >
                  <td className="px-5 py-4 font-medium text-foreground-900">{dataset.name}</td>
                  <td className="max-w-[280px] px-5 py-4 text-foreground-600">
                    <span className="line-clamp-2">{dataset.description}</span>
                  </td>
                  <td className="px-5 py-4 text-foreground-600">{dataset.source}</td>
                  <td className="px-5 py-4 text-foreground-600">{dataset.articleCount}</td>
                  <td className="px-5 py-4">
                    {dataset.status === 1 ? (
                      <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-800">
                        已处理
                      </span>
                    ) : (
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                        未处理
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-foreground-600">{dataset.createTime}</td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setForm({ name: dataset.name, description: dataset.description });
                          setDbForm({
                            dbName: dataset.dbName,
                            host: dataset.host,
                            port: dataset.port,
                            account: dataset.account,
                            password: "******",
                          });
                          setTestState("idle");
                          setDbOpen(true);
                        }}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        数据库配置
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewId(dataset.id)}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        预览
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={createOpen}
        title="创建数据集"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={goToDbConfig}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              下一步
            </button>
          </>
        }
      >
        <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="ds-name">
          名称
        </label>
        <input
          id="ds-name"
          type="text"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          placeholder="请输入数据集名称"
          className="mb-4 h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
        />
        <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="ds-desc">
          描述
        </label>
        <textarea
          id="ds-desc"
          value={form.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
          placeholder="请输入数据集描述"
          maxLength={500}
          rows={3}
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
        />
      </Modal>

      <Modal
        open={dbOpen}
        title="数据库配置"
        onClose={() => setDbOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={testConnection}
              className="mr-auto flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
            >
              <i className="ri-refresh-line text-base" />
              测试连接状态
            </button>
            <button
              type="button"
              onClick={() => setDbOpen(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={confirmDataset}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              确认修改
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[80px_1fr] sm:items-center">
          <label className="text-sm text-foreground-700" htmlFor="db-name">
            库名
          </label>
          <input
            id="db-name"
            type="text"
            value={dbForm.dbName}
            onChange={(event) => setDbForm({ ...dbForm, dbName: event.target.value })}
            placeholder="请输入数据库名称"
            className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
          />

          <label className="text-sm text-foreground-700" htmlFor="db-host">
            IP 地址
          </label>
          <input
            id="db-host"
            type="text"
            value={dbForm.host}
            onChange={(event) => setDbForm({ ...dbForm, host: event.target.value })}
            placeholder="请输入数据库服务器地址"
            className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
          />

          <label className="text-sm text-foreground-700" htmlFor="db-port">
            端口地址
          </label>
          <input
            id="db-port"
            type="text"
            value={dbForm.port}
            onChange={(event) => setDbForm({ ...dbForm, port: event.target.value })}
            placeholder="请输入端口"
            className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
          />

          <label className="text-sm text-foreground-700" htmlFor="db-account">
            用户名
          </label>
          <input
            id="db-account"
            type="text"
            value={dbForm.account}
            onChange={(event) => setDbForm({ ...dbForm, account: event.target.value })}
            placeholder="请输入数据库账号"
            className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
          />

          <label className="text-sm text-foreground-700" htmlFor="db-password">
            密码
          </label>
          <input
            id="db-password"
            type="password"
            value={dbForm.password}
            onChange={(event) => setDbForm({ ...dbForm, password: event.target.value })}
            placeholder="请输入数据库密码"
            className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
          />
        </div>

        {testState !== "idle" ? (
          <div
            className={`mt-4 flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm ${
              testState === "success"
                ? "border-accent-300 bg-accent-50 text-accent-800"
                : "border-background-300 bg-background-100 text-foreground-600"
            }`}
          >
            <i className={testState === "success" ? "ri-checkbox-circle-line" : "ri-loader-4-line"} />
            {testState === "success" ? "连接成功，可以开始数据传输" : "正在测试连接..."}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={previewId !== null}
        title={previewDataset ? `数据预览 · ${previewDataset.name}` : "数据预览"}
        onClose={() => setPreviewId(null)}
        width="max-w-3xl"
      >
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
            <div className="text-xs text-foreground-500">数据来源</div>
            <div className="mt-1 truncate text-sm font-medium text-foreground-900">
              {previewDataset?.source}
            </div>
          </div>
          <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
            <div className="text-xs text-foreground-500">存储方式</div>
            <div className="mt-1 text-sm font-medium text-foreground-900">
              {previewDataset?.saveType}
            </div>
          </div>
          <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
            <div className="text-xs text-foreground-500">文章总数</div>
            <div className="mt-1 text-sm font-medium text-foreground-900">
              {previewDataset?.articleCount}
            </div>
          </div>
          <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
            <div className="text-xs text-foreground-500">创建时间</div>
            <div className="mt-1 text-sm font-medium text-foreground-900">
              {previewDataset?.createTime}
            </div>
          </div>
        </div>
        <div className="overflow-hidden rounded-md border border-background-200">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs text-foreground-600">
                <th className="px-4 py-2.5 font-medium">文章标题</th>
                <th className="px-4 py-2.5 font-medium">来源</th>
                <th className="px-4 py-2.5 font-medium">日期</th>
                <th className="px-4 py-2.5 font-medium">长度</th>
                <th className="px-4 py-2.5 font-medium">权重</th>
              </tr>
            </thead>
            <tbody>
              {datasetSampleArticles.map((article) => (
                <tr key={article.id} className="border-b border-background-100 last:border-0">
                  <td className="px-4 py-3 text-foreground-900">{article.title}</td>
                  <td className="px-4 py-3 text-foreground-600">{article.source}</td>
                  <td className="px-4 py-3 text-foreground-600">{article.date}</td>
                  <td className="px-4 py-3 text-foreground-600">{article.words} 字符</td>
                  <td className="px-4 py-3 text-foreground-600">{article.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
}